/* pendingCheckout — bridges a plan choice across the sign-up/sign-in detour.
 *
 * Clicking a paid plan while signed out sends the visitor to /signup (or
 * /login from there) before Stripe checkout can start — there's no account
 * yet to attach a subscription to. Without remembering WHICH plan they
 * picked, they land back in the app with no idea their choice was dropped,
 * and have to go find "Upgrade plan" in Settings and pick again.
 *
 * Storage over router state for the same reason as appReturn.js: the detour
 * can cross a full page load (Signup's own "Sign in" link takes you to
 * /login), which router state doesn't survive. Both callers already await
 * these (they run inside the async login/signup handlers), so this uses the
 * plain async storage interface — no need for the sync-read cache.
 */

import { storage } from '../services/storage'

const KEY = 'jai_pending_checkout'

// Short on purpose — this only needs to bridge a single auth detour, not
// linger through a browsing session. If it's stale, silently drop it rather
// than surprise someone with a checkout redirect minutes after they signed
// in for an unrelated reason.
const MAX_AGE_MS = 15 * 60 * 1000

export async function rememberPendingCheckout(planId, billing) {
  try {
    await storage.setItem(KEY, JSON.stringify({ planId, billing, at: Date.now() }))
  } catch {
    // Storage can be unavailable (private mode, blocked cookies) — losing
    // this just means falling back to the normal post-auth landing page.
  }
}

/** Reads the pending choice AND clears it — call at most once per auth
 *  success, so a failed checkout doesn't keep retrying itself on every
 *  future sign-in. */
export async function consumePendingCheckout() {
  try {
    const raw = await storage.getItem(KEY)
    await storage.removeItem(KEY)
    if (!raw) return null
    const { planId, billing, at } = JSON.parse(raw)
    if (!planId || !at || Date.now() - at > MAX_AGE_MS) return null
    return { planId, billing }
  } catch {
    return null
  }
}
